import { ComboboxItem, isOptionsGroup } from '@mantine/core';
import { useDidUpdate } from '@mantine/hooks';
import { VSelect, VSelectProps } from '@vision/ui/components';
import { UseLazyLoadSelectOptions, useLazyLoadSelect } from '@vision/ui/hooks';
import { mergeLazyLoadSelectItems } from '@vision/ui/hooks/useLazyLoadSelect.utils';
import { OptionsGroup } from '@vision/ui/interfaces';
import { useMemo } from 'react';

interface VLazyLoadSelectProps<RequestType, ResponseType>
  extends Omit<VSelectProps, 'data'>,
    UseLazyLoadSelectOptions<RequestType, ResponseType> {
  // Listed before the loaded options; a loaded option with the same value is not repeated.
  leadingItems?: ComboboxItem[];
  onDataChange?: (data: ResponseType[]) => void;
  onSelectItemsChange?: (data: Array<ComboboxItem | OptionsGroup>) => void;
  searchKey?: string;
  resetChangeRequestParameters?: boolean;
}

export function VLazyLoadSelect<RequestType, ResponseType>({
  apiRequestParameters,
  disabledCondition,
  leadingItems,
  loading,
  multiSelectItemMapper,
  onDataChange,
  onSelectItemsChange,
  searchable = true,
  singleSelectItemMapper,
  useLazyApiQueryFunction,
  searchKey,
  filterKey,
  resetChangeRequestParameters,
  reuseCachedData,
  onDefaultValuesLoaded,
  value,
  ...props
}: VLazyLoadSelectProps<RequestType, ResponseType>) {
  // A value a leading item already labels needs no request.
  const isValueLabeled = !!value && !!leadingItems?.some((item) => item.value === value);

  const {
    allData,
    allDataSelectItems,
    contextValue: { loadMore },
    dataSelectItemsWithDisabledCondition,
    isFilterQueryLoading,
    isLoading: isLoadingLazyLoadSelect,
    setSearchQuery,
  } = useLazyLoadSelect({
    filterKey,
    searchKey,
    apiRequestParameters,
    disabledCondition,
    multiSelectItemMapper,
    singleSelectItemMapper,
    useLazyApiQueryFunction,
    resetChangeRequestParameters,
    reuseCachedData,
    onDefaultValuesLoaded,
    defaultValues: isValueLabeled ? undefined : value,
  });

  const handleLoadMore = () => {
    loadMore();
  };

  useDidUpdate(() => {
    onDataChange?.(allData);
  }, [allData]);

  useDidUpdate(() => {
    onSelectItemsChange?.(allDataSelectItems);
  }, [allDataSelectItems]);

  const data = useMemo(
    () =>
      leadingItems?.length
        ? mergeLazyLoadSelectItems(leadingItems, dataSelectItemsWithDisabledCondition)
        : dataSelectItemsWithDisabledCondition,
    [dataSelectItemsWithDisabledCondition, leadingItems],
  );

  const selectedLabel = useMemo(
    () =>
      data.flatMap((item) => (isOptionsGroup(item) ? item.items : [item])).find((item) => item.value === value)?.label,
    [data, value],
  );

  // VSelect writes the selected label into the input; like its own filter, that is not a search.
  const handleSearchChange = (query: string) => {
    setSearchQuery(query === selectedLabel ? '' : query);
  };

  const isLoading = loading || isFilterQueryLoading || isLoadingLazyLoadSelect;

  return (
    <VSelect
      data={data}
      loading={isLoading}
      value={value}
      onScrollToBottom={handleLoadMore}
      onSearchChange={handleSearchChange}
      searchable={searchable}
      clearable={true}
      {...props}
    />
  );
}
